<template>
  <div>
    <div class="dock-schedule-header d-flex align-center mb-5">
      <strong class="mr-2">{{ $t('global.specialtyTerms.interval') }}:</strong>
      <v-btn-toggle mandatory v-model="interval" divided density="compact" color="secondary">
        <v-tooltip location="top" :disabled="!disable30MinOption">
          <template v-slot:activator="{ props }">
            <div v-bind="props">
              <v-btn
                style="height: 100%"
                class="border rounded-e-sm"
                :value="30"
                :disabled="disable30MinOption">
                {{ $t('global.phrases.minuteLabel', { minutes: 30 }) }}
              </v-btn>
            </div>
          </template>
          <span>
            {{
              $t(
                'loadType.forms.loadTypeForm.tooltips.scheduleHasAtLeastOne15Or20MinInterval30MinuteViewIsDisabled'
              )
            }}
          </span>
        </v-tooltip>
        <v-tooltip location="top" :disabled="!disable20MinOption">
          <template v-slot:activator="{ props }">
            <div class="d-inline" v-bind="props">
              <v-btn
                style="height: 100%"
                :value="20"
                :disabled="disable20MinOption"
                class="border border-s-0 border-e-0">
                {{ $t('global.phrases.minuteLabel', { minutes: 20 }) }}
              </v-btn>
            </div>
          </template>
          <span>
            {{
              $t(
                'loadType.forms.loadTypeForm.tooltips.scheduleHasAtLeastOne15Or30MinInterval20MinuteViewIsDisabled'
              )
            }}
          </span>
        </v-tooltip>

        <v-tooltip location="top" :disabled="!disable15MinOption">
          <template v-slot:activator="{ props }">
            <div class="d-inline" v-bind="props">
              <v-btn
                style="height: 100%"
                :value="15"
                :disabled="disable15MinOption"
                class="border rounded-e-sm">
                {{ $t('global.phrases.minuteLabel', { minutes: 15 }) }}
              </v-btn>
            </div>
          </template>
          <span>
            {{
              $t(
                'loadType.forms.loadTypeForm.tooltips.scheduleHasAtLeastOne20MinInterval15MinuteViewIsDisabled'
              )
            }}
          </span>
        </v-tooltip>
      </v-btn-toggle>

      <v-spacer></v-spacer>
      <v-menu content-class="left-aligned-options">
        <template v-slot:activator="{ props }">
          <span ref="scheduleoptionsbtn">
            <secondary-button append-icon="mdi-chevron-down" v-bind="props">
              <span class="text-truncate">
                {{ $t('loadType.forms.loadTypeForm.buttons.setScheduleAs') }}
              </span>
            </secondary-button>
          </span>
        </template>
        <v-list density="compact" class="pb-1">
          <v-list-item class="pl-1">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  prepend-icon="mdi-hours-24"
                  density="compact"
                  size="small"
                  variant="plain"
                  class="bg-white text-left justify-start mb-0"
                  v-bind="props"
                  @click="$emit('set-schedule-default')">
                  {{ $t('loadType.forms.loadTypeForm.buttons.open247') }}
                </v-btn>
              </template>
              <span>
                {{ $t('loadType.forms.loadTypeForm.buttons.setScheduleBackToDefaultOpen247') }}
              </span>
            </v-tooltip>
          </v-list-item>
          <v-list-item class="pl-1">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  density="compact"
                  size="small"
                  variant="plain"
                  prepend-icon="mdi-cancel"
                  class="bg-white text-left justify-start mb-0"
                  v-bind="props"
                  @click="$emit('set-schedule-closed')">
                  {{ $t('loadType.forms.loadTypeForm.buttons.close247') }}
                </v-btn>
              </template>
              <span>{{ $t('loadType.forms.loadTypeForm.buttons.closeSchedule') }}</span>
            </v-tooltip>
          </v-list-item>

          <div class="option-label ml-3 mb-1 pt-2" v-if="withCopyFromDock || withCopyFromWarehouse">
            {{ $t('loadType.forms.loadTypeForm.buttons.cloneFrom') }}
          </div>

          <v-list-item class="pl-1">
            <v-tooltip location="top" v-if="withCopyFromWarehouse">
              <template v-slot:activator="{ props }">
                <v-btn
                  density="compact"
                  size="small"
                  variant="plain"
                  prepend-icon="mdi-content-copy"
                  class="bg-white text-left justify-start my-0"
                  v-bind="props"
                  @click="$emit('copy-warehouse-schedule')">
                  {{ $t('global.specialtyTerms.warehouses') }}
                </v-btn>
              </template>
              <span>{{ $t('loadType.forms.loadTypeForm.buttons.cloneTheWarehouseSchedule') }}</span>
            </v-tooltip>
          </v-list-item>

          <v-list-item class="pl-0">
            <clone-dock-schedule-dialog
              v-if="withCopyFromDock"
              @close="closeScheduleOptionsMenu"
              :docks="docks"
              @copy-schedule="emitCopyDockSchedule"></clone-dock-schedule-dialog>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="is-relative">
      <div class="table-loader__wrapper overlay-base" v-if="isInvalidTimeGrid">
        <div class="table-loader__overlay overlay-base"></div>
        <div class="table-loader__content">
          <div class="text-center mt-3">
            {{
              $t(
                'loadType.forms.loadTypeForm.messages.thisScheduleHasAConflictingCombinationOfIntervals20And1530PleaseClearScheduleToEdit'
              )
            }}.
          </div>
        </div>
      </div>
      <v-table id="schedule-table" :class="compact ? 'compact' : ''">
        <template v-slot:default>
          <thead>
            <tr id="schedule-header">
              <th class="text-center" colspan="2">{{ $t('global.basicTerms.time') }}</th>
              <th
                class="day-header"
                v-for="(weekday, index) in weekdayLabels"
                :key="weekday"
                :data-column="index + 2">
                {{ weekday }}
              </th>
            </tr>
          </thead>
          <template v-for="(hourGroup, key) in times" :key="`tbody-${key}`">
            <tbody class="hour-group">
              <tr
                v-for="(time, index) in hourGroup"
                :key="time.start"
                class="time-row"
                :class="[
                  { 'start-of-interval-group': !index || index % intervalsInHour === 0 },
                  'interval-row'
                ]">
                <th
                  class="hour-label"
                  v-if="!index || index % intervalsInHour === 0"
                  :rowspan="intervalsInHour">
                  <span class="d-inline-block full-width">
                    {{
                      convert24ToFormat(
                        time.start,
                        LuxonDateTimeFormats.Extended12HrTimeAMPM,
                        LuxonDateTimeFormats.Extended24HrTime
                      )
                    }}
                  </span>
                </th>
                <th class="row-label">
                  {{ convert24to12(time.start) }}
                </th>
                <td
                  :data-column="colIndex + 2"
                  v-for="(weekday, colIndex) in weekdays"
                  class="time-cell is-relative day-cell"
                  :class="{
                    active: isOpen(weekday, time) && !isTimeDisabled(weekday, time),
                    'time-is-disabled': isTimeDisabled(weekday, time)
                  }"
                  :data-start="time.start"
                  :data-end="time.end"
                  :key="time.start + weekday"
                  @mousedown="
                    e => {
                      handleCellClick(e, weekday, time, isTimeDisabled(weekday, time));
                    }
                  ">
                  <v-icon class="add-interval" size="x-small">mdi-plus</v-icon>
                  <v-icon class="subtract-interval" size="x-small">mdi-minus</v-icon>
                  <div class="disabled-time-interval overlay align-center justify-center">
                    <v-icon size="x-small" class="mr-1">mdi-cancel</v-icon>
                    {{ $t('global.specialtyTerms.warehouses') }}
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </template>
      </v-table>
    </div>
    <div class="d-flex mt-2 align-center">
      <div class="mr-4 d-flex align-center">
        <div class="circle bordered bg-primary pa-1 mr-1"></div>
        <strong class="font-size-x-small">{{ $t('global.basicTerms.open').toUpperCase() }}</strong>
      </div>
      <div class="d-flex align-center">
        <div class="circle pa-1 mr-1 bordered"></div>
        <strong class="font-size-x-small">
          {{ $t('global.basicTerms.closed').toUpperCase() }}
        </strong>
      </div>
    </div>
  </div>
</template>

<script>
import {
  clockToDateTime,
  formatDateTimeWithMilitarySupport,
  getClosedScheduleTemplate,
  getFullyOpenSchedule,
  LuxonDateTimeFormats,
  clocksOverlap,
  mergeAdjacentSpans,
  objPropExists
} from '@nova/core';
import { debounce } from 'lodash';
import { DateTime, Info } from 'luxon';

/**
 * @displayName Time Select Grid
 */
export default {
  props: {
    /**
     * @model
     */
    modelValue: {
      type: Object,
      required: false,
      default: () => ({})
    },
    /**
     * Display in compact mode
     */
    compact: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Allow edit toggle
     */
    toggleToEdit: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * The component should default to edit mode
     */
    shouldStartInEditMode: {
      type: Boolean,
      required: false,
      default: false
    },
    enabledIntervals: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    localStorageKey: {
      type: String,
      required: false
    },
    docks: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    withCopyFromWarehouse: {
      type: Boolean,
      required: false,
      default: true
    },
    withCopyFromDock: {
      type: Boolean,
      required: false,
      default: true
    },
    settingsEntity: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  data() {
    return {
      LuxonDateTimeFormats,
      editMode: false,
      times: [],
      modified: false,
      weekdays: [],
      originalSchedule: {},
      internalSchedule: {},
      interval: 30,
      disable30MinOption: false,
      disable20MinOption: false,
      disable15MinOption: false,
      isInvalidTimeGrid: false,
      shouldShowDocksDialog: false
    };
  },
  computed: {
    intervalsInHour() {
      return 60 / this.interval;
    },
    weekdayLabels() {
      return Array.from({ length: 7 }, (_, i) => {
        // Make Sunday (0) the start of the week
        const weekday = ((i + 6) % 7) + 1;
        return DateTime.fromObject({ weekday }).setLocale(this.$i18n.locale).toFormat('ccc');
      });
    }
  },
  methods: {
    emitCopyDockSchedule(schedule) {
      this.$emit('copy-dock-schedule', schedule);
    },
    /**
     * Convert time from 24 hour to 12 hour format
     * @public
     * @param {string} time
     * @returns {string}
     */
    convert24to12(time) {
      return this.convert24ToFormat(
        time,
        LuxonDateTimeFormats.Extended12HrTimeAMPM,
        LuxonDateTimeFormats.Extended24HrTime
      );
    },
    convert24ToFormat(time, returnFormat12, returnFormat24) {
      if (typeof time === 'string') {
        const tz = null;
        if (time.length === 4) {
          time = `0${time}`; // e.g. 9:00 -> 09:00
        }
        return formatDateTimeWithMilitarySupport(
          time,
          tz,
          returnFormat12,
          this.$isMilitaryTimeEnabled(this.settingsEntity),
          returnFormat24,
          this.$i18n.locale
        );
      }
    },
    /**
     * Check if any spans overlap
     * @public
     * @param weekday
     * @param span
     * @returns {boolean|*}
     */
    isOpen(weekday, span) {
      let dayKey = weekday.toLowerCase();

      if (this.internalSchedule[dayKey]) {
        return this.internalSchedule[dayKey].some(clockSpan => {
          return clocksOverlap(clockSpan, span);
        });
      }

      return false;
    },
    isTimeDisabled(weekday, span) {
      const hasEnabledIntervals = this.enabledIntervals
        ? Object.keys(this.enabledIntervals).length > 0
        : false;
      let isDisabled = hasEnabledIntervals;
      if (hasEnabledIntervals) {
        const intervals = this.enabledIntervals[weekday.toLowerCase()];
        if (intervals) {
          intervals.forEach(clockSpan => {
            if (clocksOverlap(clockSpan, span)) {
              isDisabled = false;
            }
          });
        }
      }

      return isDisabled;
    },
    /**
     * Handle a table cell click
     * @public
     * @param event
     * @param day
     * @param slot
     */
    handleCellClick(event, day, slot, isDisabled) {
      event.preventDefault();
      // Do nothing if the event is not correct
      if (event.buttons < 1 && event.type !== 'click') {
        return;
      }

      let noOverlap = true;
      let daySchedule = this.internalSchedule[day.toLowerCase()];

      // If an exact match exists already, remove
      let existingIdx = daySchedule.findIndex(s => this.spansAreEqual(s, slot));
      if (existingIdx > -1) {
        daySchedule.splice(existingIdx, 1);
        this.hasLesserIntervals(this);
        return;
      }
      daySchedule.forEach(span => {
        if (clocksOverlap(slot, span)) {
          noOverlap = false;
          this.removeSubSpan(slot, span, daySchedule);
        }
      });

      if ((!daySchedule.length || noOverlap) && !isDisabled) {
        daySchedule.push(slot);
      }

      this.internalSchedule[day.toLowerCase()] = mergeAdjacentSpans(daySchedule);

      this.modified = true;
      this.hasLesserIntervals(this);
    },
    /**
     * Check if spans are equal duration
     * @public
     * @param {object} span1
     * @param {object} span2
     * @returns {boolean}
     */
    spansAreEqual(span1, span2) {
      return span1.start === span2.start && span1.end === span2.end;
    },
    /**
     * Remove a span from span list
     * @public
     * @param {object} subSpan
     * @param {object} parentSpan
     * @param {object[]} spanList
     * @returns {object[]}
     */
    removeSubSpan(subSpan, parentSpan, spanList) {
      if (parentSpan.start === subSpan.start) {
        const startDateTime = clockToDateTime(parentSpan.start);
        if (!startDateTime) {
          return spanList;
        }
        parentSpan.start = startDateTime.plus({ minutes: this.interval }).toFormat('H:mm');
      } else if (parentSpan.end === subSpan.end) {
        const endDateTime =
          parentSpan.end === '23:59'
            ? clockToDateTime('0:00')?.plus({ days: 1 })
            : clockToDateTime(parentSpan.end);
        if (!endDateTime) {
          return spanList;
        }
        parentSpan.end = endDateTime.minus({ minutes: this.interval }).toFormat('H:mm');
      } else {
        let idx = spanList.findIndex(s => s.start === parentSpan.start);

        if (idx > -1) {
          spanList.splice(idx, 1);
          spanList.push(
            {
              start: parentSpan.start,
              end: subSpan.start
            },
            {
              start: subSpan.end,
              end: parentSpan.end
            }
          );
        }
      }

      return spanList;
    },
    /**
     * Build clock spans array based on epoch
     * @public
     * @param {number} interval
     * @returns {object[]}
     */
    buildClockSpans(interval = 30) {
      let dt = DateTime.fromISO('1970-01-01T00:00:00', { zone: 'utc' });
      let times = {};
      while (dt.ordinal === 1) {
        const hour = String(dt.startOf('hour').hour);
        let spanObj = { start: dt.toFormat('H:mm') };
        dt = dt.plus({ minutes: interval });
        let end = dt.toFormat('H:mm');
        spanObj.end = end === '0:00' ? '23:59' : end;
        if (!objPropExists(times, `hour-${hour}`)) {
          times[`hour-${hour}`] = [];
        }
        times[`hour-${hour}`].push(spanObj);
      }
      return times;
    },
    /**
     * Set original schedule and internal schedule using v-model value
     * @public
     */
    mapLocalSchedule() {
      this.originalSchedule = JSON.parse(JSON.stringify(this.modelValue));
      this.internalSchedule = {
        ...JSON.parse(JSON.stringify(getClosedScheduleTemplate())),
        ...(this.modelValue && Object.keys(this.modelValue).length > 1
          ? this.modelValue
          : getFullyOpenSchedule())
      };
    },
    /**
     * Save edit changes
     * @public
     */
    saveChanges() {
      this.$emit('update-interval', this.interval);
      this.$emit('update:modelValue', this.internalSchedule);
      this.originalSchedule = { ...this.internalSchedule };
      this.editMode = false;
    },
    /**
     * Toggle to edit mode
     * @public
     */
    enterEditMode() {
      this.editMode = true;
      this.modified = false;
    },
    /**
     * Cancel edit mode
     * @public
     */
    cancelEdit() {
      this.$emit('close');
      this.internalSchedule = {
        ...JSON.parse(JSON.stringify(getClosedScheduleTemplate())),
        ...JSON.parse(JSON.stringify(this.originalSchedule))
      };
      this.editMode = false;
    },
    hasLesserIntervals: debounce(vueContext => {
      let hasFifteenMinIntervals = false;
      let hasTwentyMinIntervals = false;
      let hasThirtyMinIntervals = false;
      const fifteenMinIdentifiers = [':15', ':45'];
      const twentyMinIdentifiers = [':20', ':40'];
      const thirtyMinIdentifiers = [':30'];
      Object.values(vueContext.internalSchedule).forEach(daySchedule => {
        if (Array.isArray(daySchedule)) {
          daySchedule.forEach(interval => {
            if (
              fifteenMinIdentifiers.some(minute => interval.start.includes(minute)) ||
              fifteenMinIdentifiers.some(minute => interval.end.includes(minute))
            ) {
              hasFifteenMinIntervals = true;
            } else if (
              twentyMinIdentifiers.some(minute => interval.start.includes(minute)) ||
              twentyMinIdentifiers.some(minute => interval.end.includes(minute))
            ) {
              hasTwentyMinIntervals = true;
            } else if (
              thirtyMinIdentifiers.some(minute => interval.start.includes(minute)) ||
              thirtyMinIdentifiers.some(minute => interval.end.includes(minute))
            ) {
              hasThirtyMinIntervals = true;
            }
          });
        }
      });
      if (
        (hasFifteenMinIntervals && hasTwentyMinIntervals) ||
        (hasThirtyMinIntervals && hasTwentyMinIntervals)
      ) {
        vueContext.isInvalidTimeGrid = true;
        return;
      } else {
        vueContext.isInvalidTimeGrid = false;
      }
      if (hasTwentyMinIntervals) {
        vueContext.updateInterval({
          interval: 20,
          disable30MinOption: true,
          disable20MinOption: false,
          disable15MinOption: true
        });
      } else if (hasThirtyMinIntervals) {
        if (hasFifteenMinIntervals) {
          vueContext.updateInterval({
            interval: 15,
            disable30MinOption: true,
            disable20MinOption: true,
            disable15MinOption: false
          });
        } else {
          vueContext.updateInterval({
            interval: vueContext.interval,
            disable30MinOption: false,
            disable20MinOption: true,
            disable15MinOption: false
          });
        }
      } else if (hasFifteenMinIntervals) {
        vueContext.updateInterval({
          interval: 15,
          disable30MinOption: true,
          disable20MinOption: true,
          disable15MinOption: false
        });
      } else {
        vueContext.updateInterval({
          interval: vueContext.interval,
          disable30MinOption: false,
          disable20MinOption: false
        });
      }
    }),
    rowLabelMouseEnter(e) {
      const target = e.target;
      target.classList.add('time-row-group-item');
      target.parentElement.querySelectorAll('td').forEach(node => {
        node.classList.add('time-row-group-item');
      });
    },
    rowLabelMouseLeave(e) {
      const target = e.target;
      target.classList.remove('time-row-group-item');
      target.parentElement.querySelectorAll('td').forEach(node => {
        node.classList.remove('time-row-group-item');
      });
    },
    rowLabelClick(e) {
      this.handleGroupClick(e.target.parentElement);
    },
    hourLabelMouseEnter(e) {
      e.target.closest('.hour-group').classList.add('active');
    },
    hourLabelMouseLeave(e) {
      e.target.closest('.hour-group').classList.remove('active');
    },
    hourLabelClick(e) {
      this.handleGroupClick(e.target.closest('.hour-group'));
    },
    handleGroupClick(parentEl, additionalSelector = '') {
      const selector = additionalSelector ? `td${additionalSelector}` : 'td';
      const activeCellCount = parentEl.querySelectorAll(`${selector}.active`).length;
      const disabledIntervalCount = Array.from(
        parentEl.querySelectorAll(`${selector} .disabled-time-interval`)
      ).filter(node => {
        return window.getComputedStyle(node, null).display !== 'none';
      }).length;
      const totalPotentiallyActiveCells =
        parentEl.querySelectorAll(selector).length - disabledIntervalCount;
      const shouldSelect = activeCellCount > 0 && activeCellCount < totalPotentiallyActiveCells;
      if (shouldSelect) {
        parentEl.querySelectorAll(`${selector}:not(.active)`).forEach(node => {
          node.dispatchEvent(new Event('mousedown'));
        });
      } else {
        parentEl.querySelectorAll(selector).forEach(node => {
          node.dispatchEvent(new Event('mousedown'));
        });
      }
    },
    dayHeaderMouseEnter(e) {
      const target = e.target;
      const column = target.getAttribute('data-column');
      target.classList.add('highlight');
      document
        .getElementById('schedule-table')
        .querySelectorAll(`td[data-column="${column}"]`)
        .forEach(node => node.classList.add('highlight'));
    },
    dayHeaderMouseLeave(e) {
      const target = e.target;
      const column = target.getAttribute('data-column');
      target.classList.remove('highlight');
      document
        .getElementById('schedule-table')
        .querySelectorAll(`td[data-column="${column}"]`)
        .forEach(node => node.classList.remove('highlight'));
    },
    dayHeaderClick(e) {
      const target = e.target;
      const column = target.getAttribute('data-column');
      this.handleGroupClick(document.getElementById('schedule-table'), `[data-column="${column}"]`);
    },
    addEventListeners() {
      document.querySelectorAll('.row-label').forEach(node => {
        node.addEventListener('mouseenter', this.rowLabelMouseEnter);
        node.addEventListener('mouseleave', this.rowLabelMouseLeave);
        node.addEventListener('click', this.rowLabelClick);
      });
      document.querySelectorAll('.hour-label').forEach(node => {
        node.addEventListener('mouseenter', this.hourLabelMouseEnter);
        node.addEventListener('mouseleave', this.hourLabelMouseLeave);
        node.addEventListener('click', this.hourLabelClick);
      });
      document.querySelectorAll('.day-header').forEach(node => {
        node.addEventListener('mouseenter', this.dayHeaderMouseEnter);
        node.addEventListener('mouseleave', this.dayHeaderMouseLeave);
        node.addEventListener('click', this.dayHeaderClick);
      });
    },
    removeEventListeners() {
      document.querySelectorAll('.row-label').forEach(node => {
        node.removeEventListener('mouseenter', this.rowLabelMouseEnter);
        node.removeEventListener('mouseleave', this.rowLabelMouseLeave);
        node.removeEventListener('click', this.rowLabelClick);
      });
      document.querySelectorAll('.hour-label').forEach(node => {
        node.removeEventListener('mouseenter', this.hourLabelMouseEnter);
        node.removeEventListener('mouseleave', this.hourLabelMouseLeave);
        node.removeEventListener('click', this.hourLabelClick);
      });
      document.querySelectorAll('.day-header').forEach(node => {
        node.removeEventListener('mouseenter', this.dayHeaderMouseEnter);
        node.removeEventListener('mouseleave', this.dayHeaderMouseLeave);
        node.removeEventListener('click', this.dayHeaderClick);
      });
    },
    updateInterval({ interval, disable30MinOption, disable20MinOption, disable15MinOption }) {
      this.interval = interval;
      this.disable30MinOption = disable30MinOption;
      this.disable20MinOption = disable20MinOption;
      this.disable15MinOption = disable15MinOption;
      if (this.localStorageKey) {
        localStorage.setItem(`scheduleInterval-${this.localStorageKey}`, this.interval);
      }
    },
    closeScheduleOptionsMenu() {
      this.$refs.scheduleoptionsbtn.getElementsByTagName('button')[0].click();
    }
  },
  watch: {
    modelValue() {
      this.$nextTick(() => {
        this.mapLocalSchedule();
        this.hasLesserIntervals(this);
      });
    },
    interval() {
      this.times = this.buildClockSpans(this.interval);

      if (this.localStorageKey) {
        localStorage.setItem(`scheduleInterval-${this.localStorageKey}`, this.interval);
      }
      this.$nextTick(() => {
        this.removeEventListeners();
        this.addEventListeners();
      });
    },
    localStorageKey() {
      const lsInterval = localStorage.getItem(`scheduleInterval-${this.localStorageKey}`);
      if (lsInterval) {
        this.interval = parseInt(lsInterval);
      }
    },
    '$i18n.locale'() {
      this.removeEventListeners();
      this.$nextTick(() => this.addEventListeners());
    }
  },
  mounted() {
    this.mapLocalSchedule();
    const luxonWeekdays = Info.weekdays('long', { locale: 'en' });
    this.weekdays = [luxonWeekdays[6], ...luxonWeekdays.slice(0, 6)];
    this.times = this.buildClockSpans(this.interval);
    if (this.shouldStartInEditMode) {
      this.enterEditMode();
    }
    this.$nextTick(() => this.addEventListeners());
    this.hasLesserIntervals(this);
  },
  beforeUnmount() {
    this.removeEventListeners();
  }
};
</script>

<style lang="scss" scoped>
.v-list-item--density-compact.v-list-item--one-line {
  min-height: auto;
}
</style>
